import { useContext, useEffect, useRef } from 'react'
import { FormContext } from '../context'
import type { Rule } from '../Validations'

interface UseInputValueProps {
  name: string
  defaultValue?: number | string | boolean
  falseValue: string | boolean | number
  multiple?: boolean
  trueValue?: string | boolean | number
  setInputValue: (value: number | string | boolean | Array<string | number>) => void
  validations?: Array<ReturnType<Rule>>
  disabled?: boolean
  onClear?: () => void
}

export default function useInputValue({
  name,
  defaultValue,
  multiple,
  trueValue,
  falseValue,
  validations,
  setInputValue,
  disabled = false,
  onClear,
}: UseInputValueProps) {
  const {
    form,
    setFormValue,
    setFormArrayValue,
    removeFormArrayValue,
    setFormValidation,
    setFormItemMetaData,
  } = useContext(FormContext)
  const hasToSync = useRef(true)
  const hasDefaultValue = useRef(0)

  const metaName = [name, trueValue].filter(Boolean).join('-')

  const onInputChange = (newValue: string | boolean | number | undefined) => {
    hasToSync.current = false

    if (disabled) return

    if (multiple) {
      if (newValue) {
        setFormArrayValue?.(name, trueValue ?? newValue, true)
        setFormItemMetaData(metaName, {
          hasValue: true,
        })
      } else {
        removeFormArrayValue?.(name, trueValue ?? newValue ?? '', true)
        setFormItemMetaData(metaName, {
          hasValue: false,
        })
      }
    } else {
      setFormValue?.(name, newValue ? (trueValue ?? newValue) : (falseValue ?? newValue), true)
      setFormItemMetaData(metaName, {
        hasValue: trueValue ? newValue === trueValue : !!newValue,
      })
    }
  }

  useEffect(() => {
    const value = form[name]

    if (!hasToSync.current || disabled) {
      hasToSync.current = true
      return
    }

    const hasBeenInitialized = hasDefaultValue.current > 1

    if (defaultValue === undefined && value === undefined) {
      setFormValue?.(name, multiple ? [] : falseValue, true)
      setFormItemMetaData(metaName, {
        hasValue: false,
      })
    } else if (defaultValue !== undefined && !hasBeenInitialized) {
      if (multiple) {
        setFormArrayValue?.(name, defaultValue ? trueValue : undefined, false)
        setFormItemMetaData(metaName, {
          initialized: true,
          hasValue: true,
        })
      } else {
        setFormValue?.(
          name,
          defaultValue ? (trueValue ?? defaultValue) : (falseValue ?? defaultValue),
          false
        )
        setFormItemMetaData(metaName, {
          initialized: true,
          hasValue: trueValue ? defaultValue === trueValue : !!defaultValue,
        })
      }
      setInputValue(defaultValue)
      hasDefaultValue.current += 1
    } else if (!hasBeenInitialized) {
      setInputValue(value)
      hasDefaultValue.current += 1
    } else if (value === undefined) {
      setInputValue(falseValue)
      setFormValue?.(name, multiple ? [] : falseValue, true)
      setFormItemMetaData(metaName, {
        hasValue: false,
      })
      onClear?.()
    } else {
      setInputValue(value)
    }
  }, [
    form,
    name,
    defaultValue,
    setFormValue,
    multiple,
    trueValue,
    falseValue,
    setFormArrayValue,
    setFormItemMetaData,
    setInputValue,
    metaName,
    disabled,
    onClear,
  ])

  useEffect(() => {
    const value = form[name]
    const hasBeenInitialized = hasDefaultValue.current > 0

    if (hasBeenInitialized && hasToSync.current) {
      if (multiple && Array.isArray(value)) {
        setFormItemMetaData(metaName, {
          hasValue: value?.some(item => item === trueValue) ?? false,
          initialized: true,
        })
      } else {
        let hasValue = !!value

        if (trueValue) {
          hasValue = value === trueValue
        }

        if (falseValue) {
          hasValue = value !== falseValue
        }

        setFormItemMetaData(metaName, {
          hasValue,
          initialized: true,
        })
      }
    }
  }, [form, name, trueValue, falseValue, metaName, setFormItemMetaData, multiple])

  useEffect(() => {
    setFormItemMetaData(metaName, {
      initialized: false,
    })

    if (!validations) return

    setFormValidation(name, validations)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    onInputChange,
  }
}
